import { format } from 'date-fns';
import { hasCrmChangeConditions } from '@/containers/playbooks/custom-builder/config/crm-change-conditions';
import { pollMinutes } from '@/containers/playbooks/custom-builder/poll-interval';
import type {
  CustomCrmTriggerConfig,
  SheetFilters,
} from '@/types/custom-playbooks';
import type { TriggerDto } from '@/types/gtm-agents';
import { scheduleWords, timeAgo } from './agent-row.util';
import { triggerTitle } from './triggers.util';

/**
 * One name for an agent's limits wherever they show: Settings, the candidate
 * preview, Run now's confirm and the turn-on line. The credit limit counts in
 * the agent's window (the API's `capWindow`, "Credits per week"); records
 * count each run.
 */

/** The window of an agent with no scheduled play (the API's `ROLLING_LABEL`). */
export const CREDIT_WINDOW_FALLBACK = 'Credits per 24 hours';

/** The records limit: one run, whatever the schedule. */
export const RECORDS_PER_RUN = 'Records per run';

/** The credit limit's name: its window, "Credits per 24 hours" when none came. */
export function creditWindowLabel(
  capWindow: string | null | undefined
): string {
  return capWindow || CREDIT_WINDOW_FALLBACK;
}

/**
 * The play poll's next tick after `now` for a play on every `everyMinutes`:
 * every 30 minutes runs at :00 and :30 UTC (the API's `next_poll_check`),
 * every 5 at :00, :05 and so on, so the play checks by then.
 */
export function nextPollCheck(now: Date, everyMinutes = 30): Date {
  const at = new Date(now);
  at.setUTCSeconds(0, 0);
  at.setUTCMinutes(
    at.getUTCMinutes() - (at.getUTCMinutes() % everyMinutes) + everyMinutes
  );
  return at;
}

/**
 * What the trigger takes first, by the rules the poll reads it with (the
 * API's property_backfill, crm_all_matches and is_hubspot_list_trigger): a
 * CRM change trigger takes new changes only whatever its backfill, all
 * matches reads every matching contact or lead each period, a list read with
 * no watermark (a Salesforce list or a HubSpot company list) takes every
 * member, and otherwise the backfill decides. An Attio list of people or
 * companies reads past a watermark, as a HubSpot people list does, and never
 * reads all matches (the API's crm_all_matches takes HubSpot lists only).
 */
function firstPickup(config: TriggerDto['config'] | undefined): string {
  const crm = config?.source === 'crm' ? config.crm : undefined;
  if (crm && typeof crm === 'object' && !Array.isArray(crm)) {
    const {
      conditions,
      conditionType,
      crmType,
      objectType,
      scope,
      perPeriod,
      backfill,
    } = crm as Partial<CustomCrmTriggerConfig>;
    if (hasCrmChangeConditions(conditions)) return 'New changes only';
    const type = String(crmType ?? '').toLowerCase();
    const hubspot = type === 'hubspot';
    const list = conditionType === 'list_membership';
    if (
      scope === 'all_matches' &&
      (objectType === 'Contact' || objectType === 'Lead') &&
      (!list || hubspot)
    )
      return typeof perPeriod === 'number' && perPeriod > 0
        ? `Up to ${amount(perPeriod)} matching records each period`
        : 'All matching records each period';
    const watermarked =
      (hubspot && objectType !== 'Account') || type === 'attio';
    if (list && !watermarked) return 'All list members first';
    return backfill === 'historic'
      ? 'All matching records first'
      : 'New records only';
  }
  // A Sheet on every row each period rereads the whole tab each period (the
  // API's sheet all_rows), up to its per-period rows when set.
  const sheet =
    config?.source === 'google_sheet'
      ? (config.sheet as Partial<SheetFilters> | undefined)
      : undefined;
  if (sheet?.scope === 'all_rows')
    return typeof sheet.perPeriod === 'number' && sheet.perPeriod > 0
      ? `Up to ${amount(sheet.perPeriod)} rows each period`
      : 'All rows each period';
  const historic = Object.values(config ?? {}).some(
    (block) => (block as { backfill?: unknown } | null)?.backfill === 'historic'
  );
  return historic ? 'All matching records first' : 'New records only';
}

/** Triggers that wait to be sent records instead of reading a source. */
const UNPOLLED: ReadonlySet<string> = new Set(['webhook', 'play_handoff']);

const amount = (n: number) =>
  n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * What turning on does, in one line: the trigger, what it picks up first,
 * when it next runs or checks, and the credit limit in its window ("On: HubSpot
 * record matches, every 30 minutes. All matching records first. Next check by
 * 14:30. Credits per 24 hours: 500."). Trigger facts only for one play.
 */
export function turnOnLine({
  trigger,
  capWindow,
  creditLimit,
  nextRunAt,
  now = new Date(),
}: {
  trigger?: Pick<TriggerDto, 'kind' | 'config' | 'schedule'>;
  capWindow: string | null | undefined;
  creditLimit: number | null | undefined;
  nextRunAt?: string | null;
  now?: Date;
}): string {
  const polled = Boolean(trigger?.kind && !UNPOLLED.has(trigger.kind));
  const continuous =
    !trigger?.schedule || trigger.schedule.frequency === 'continuous';
  const when = scheduleWords(trigger?.schedule);
  const next = nextRunAt
    ? 'Next run ' + timeAgo(nextRunAt, now)
    : polled && continuous
      ? 'Next check by ' +
        format(nextPollCheck(now, pollMinutes(trigger?.schedule)), 'HH:mm')
      : null;
  return [
    trigger
      ? polled
        ? `On: ${triggerTitle(trigger)}, ${when[0].toLowerCase() + when.slice(1)}`
        : `On: ${triggerTitle(trigger)}`
      : 'On',
    polled && firstPickup(trigger?.config),
    next,
    creditLimit != null
      ? `${creditWindowLabel(capWindow)}: ${amount(creditLimit)}`
      : 'No credit limit',
  ]
    .filter(Boolean)
    .map((part) => part + '.')
    .join(' ');
}

/**
 * Run now's confirm, label plus state: the records and credits it can use
 * (the agent's limits, the credit limit in its window; a reused Sample's
 * records at no new credits), the CRM it writes to, and, for an Off play with
 * an Approve step, when its approvals can be decided.
 */
export function runNowLines({
  recordsLimit,
  creditLimit,
  capWindow,
  crm,
  reuse,
  approvalsWait,
}: {
  recordsLimit: number | null | undefined;
  creditLimit: number | null | undefined;
  /** The API's `capWindow` ("Credits per week"); none reads the rolling one. */
  capWindow?: string | null;
  crm: string | null;
  reuse?: { records: number };
  approvalsWait: boolean;
}): string[] {
  const windowLabel = creditWindowLabel(capWindow);
  return [
    reuse
      ? `${amount(reuse.records)} tested records`
      : recordsLimit != null
        ? `Up to ${amount(recordsLimit)} records`
        : null,
    reuse
      ? 'No new credits'
      : creditLimit != null
        ? `Up to ${amount(creditLimit)} ${windowLabel[0].toLowerCase() + windowLabel.slice(1)}`
        : null,
    crm ? `Writes to ${crm}` : null,
    approvalsWait
      ? 'Approvals from this run can be decided once the agent is on'
      : null,
  ].filter((line): line is string => line !== null);
}
